import React, { useContext, useState } from 'react';
import { Box, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { InfluencerDataContext } from '../../contexts/InfluencerDataContext';
import FrostedSupportButton from '../../components/FrostedSupportButton';
import useCrisp from '../../hooks/useCrisp';
import WalletBalanceHero from './components/WalletBalanceHero';
import WalletEarningsCard from './components/WalletEarningsCard';
import WalletRecentActivity from './components/WalletRecentActivity';
import WalletWithdrawals from './components/WalletWithdrawals';
import WalletWhopWithdrawSection from './components/WalletWhopWithdrawSection';
import { useWalletPendingBreakdown } from './hooks/useWalletPendingBreakdown';
import { useWalletBootstrap } from './hooks/useWalletBootstrap';
import { useSetupPayouts } from './hooks/useSetupPayouts';
import {
  resolveEffectiveWhopCompanyId,
  type SetupWhopCompanyBinding,
} from './utils/walletSetupCompanyUtils';
import { ff } from '../../theme';
import { DisplayHeading } from '../../components/ui';
import { walletColors, walletPageShellSx } from './walletTheme';

const CONNECT_WHOP_ERROR_PREFIX = 'Please connect your Whop account';

export interface WalletPageWithPayoutPortalProps {
  /** Rendered inside Profile edit flow — hides title, full-bleed grey shell. */
  embedded?: boolean;
}

/**
 * Wallet screen — Figma 6430:7 layout; balance from API, Whop Elements for verify/withdraw.
 */
const WalletPageWithPayoutPortal: React.FC<WalletPageWithPayoutPortalProps> = ({ embedded = false }) => {
  const { influencerData, isSwitchedAccount } = useContext(InfluencerDataContext);
  const blockCreatorPayoutActions = isSwitchedAccount;
  const { openCrisp } = useCrisp(influencerData?.email_address);
  const influencerId = influencerData?.id;
  const [setupBinding, setSetupBinding] = useState<SetupWhopCompanyBinding | undefined>(undefined);
  const effectiveWhopCompanyId = resolveEffectiveWhopCompanyId(
    influencerData?.whopCompanyId,
    influencerId,
    setupBinding,
  );

  const {
    session: walletSession,
    kycApproved,
    transferredAmount,
    failedTransferCount,
    pendingBreakdown,
    pendingItems,
    pendingDataLoaded,
    loading,
    balanceRefreshing,
    error: bootstrapError,
    refreshAvailableBalance,
    setKycApproved,
  } = useWalletBootstrap({
    influencerId,
    whopCompanyId: effectiveWhopCompanyId,
  });
  const { breakdown: resolvedPendingBreakdown, items: resolvedPendingItems } =
    useWalletPendingBreakdown({
      bootstrapBreakdown: pendingBreakdown,
      bootstrapItems: pendingItems,
      bootstrapLoaded: pendingDataLoaded,
    });
  const [withdrawalsRefreshKey, setWithdrawalsRefreshKey] = useState(0);

  const handleWithdrawalsRefresh = () => {
    setWithdrawalsRefreshKey((key) => key + 1);
  };

  const { setupPayouts, connecting, connectError } = useSetupPayouts({
    influencerId,
    setKycApproved,
    onCompanyId: (companyId) => {
      if (influencerId) {
        setSetupBinding({ influencerId, companyId });
      }
    },
    onWithdrawalComplete: () => {
      refreshAvailableBalance();
      handleWithdrawalsRefresh();
    },
  });
  const error =
    connectError ??
    bootstrapError ??
    (walletSession?.requiresHttps
      ? 'The payout portal requires HTTPS. Use your production or staging URL to access the wallet.'
      : null);

  const needsWhopConnection =
    !effectiveWhopCompanyId || (bootstrapError?.includes(CONNECT_WHOP_ERROR_PREFIX) ?? false);

  const canEmbedWhop =
    !needsWhopConnection &&
    !error &&
    walletSession &&
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    Boolean(walletSession.token && walletSession.companyId);

  const hasWhopAccount = Boolean(effectiveWhopCompanyId) && !needsWhopConnection;

  const heroProps = {
    availableBalance: walletSession?.availableBalance,
    balanceLoading: loading || balanceRefreshing,
    pendingBreakdown: resolvedPendingBreakdown,
    hasWhopAccount,
    withdrawEnabled: canEmbedWhop && !blockCreatorPayoutActions,
    connecting,
    onConnect: blockCreatorPayoutActions
      ? undefined
      : () => {
          void setupPayouts();
        },
  };

  const shellSx = walletPageShellSx;

  const walletHeader = !embedded ? (
    <Flex align="center" justify="space-between" pt="26px" pb="16px">
      <DisplayHeading size="34px">Wallet</DisplayHeading>
      <FrostedSupportButton onClick={openCrisp} />
    </Flex>
  ) : null;

  if (loading && effectiveWhopCompanyId && !needsWhopConnection) {
    return (
      <Box {...shellSx} pb={embedded ? '24px' : '80px'} position="relative">
        {walletHeader}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flex={1}
          minH={walletHeader ? '40vh' : '50vh'}
        >
          <VStack spacing={3}>
            <Spinner color={ff.ink} />
            <Text fontFamily="display" color={walletColors.textSecondary}>Loading wallet…</Text>
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box {...shellSx} pb={embedded ? '24px' : '80px'}>
      <Box px={4} maxW={embedded ? '800px' : undefined} mx={embedded ? 'auto' : undefined} w="100%" fontFamily="display">
        {walletHeader}

        <VStack spacing="20px" align="stretch" pt={embedded ? 2 : 0}>
        {error && !needsWhopConnection && (
          <Box bg="red.50" border="1px solid" borderColor="red.100" borderRadius="20px" p={4}>
            <Text fontSize="14px" color={ff.danger}>
              {error}
            </Text>
          </Box>
        )}

        {needsWhopConnection && error && (
          <Box bg={ff.limeSurface} borderRadius="20px" p={4}>
            <Text fontSize="14px" color={ff.ink}>
              {error}
            </Text>
          </Box>
        )}

        {canEmbedWhop && walletSession ? (
          <WalletWhopWithdrawSection
            heroProps={heroProps}
            companyId={walletSession.companyId}
            accessToken={walletSession.token}
            kycApproved={kycApproved}
            onKycApprovalChange={setKycApproved}
            influencerId={influencerData?.id}
            hasWhopAccount={hasWhopAccount}
            withdrawalsRefreshKey={withdrawalsRefreshKey}
            onRefreshBalance={refreshAvailableBalance}
            onRefreshWithdrawals={handleWithdrawalsRefresh}
          />
        ) : (
          <>
            <WalletBalanceHero {...heroProps} />
            <WalletWithdrawals enabled={hasWhopAccount} refreshKey={withdrawalsRefreshKey} />
          </>
        )}

        {transferredAmount != null && transferredAmount > 0 && (
          <Text fontSize="14px" color={ff.body} fontWeight="500" px={1}>
            ${transferredAmount.toFixed(2)} was moved to your Whop balance from completed work.
          </Text>
        )}

        {failedTransferCount > 0 && (
          <Box bg={ff.cream} borderRadius="20px" p={4}>
            <Text fontSize="14px" color={ff.ink}>
              {failedTransferCount} payout{failedTransferCount === 1 ? '' : 's'} could not be transferred.
              Refresh this page to retry, or contact support if this continues.
            </Text>
          </Box>
        )}

        <WalletEarningsCard />
        <WalletRecentActivity
          pendingItems={resolvedPendingItems}
          payoutSweepAmount={transferredAmount}
        />
        </VStack>
      </Box>
    </Box>
  );
};

export default WalletPageWithPayoutPortal;
